import React, { ElementType, RefObject } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Search, RefreshCw, MessageCircleMore, Clock3, Settings } from 'lucide-react';

export const waAccent = '#128C7E';
export const waBubbleOutgoing = '#DCF8C6';
export const waSurface = 'white';
export const waBorder = 'gray.200';
export const waThreadBg = '#EFEAE2';

export const formatInboxTime = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

export const formatInboxDay = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export const isWindowExpired = (value?: string | null) =>
  Boolean(value && new Date(value).getTime() < Date.now());

export const WhatsAppInboxShell = ({
  sidebar,
  thread,
}: {
  sidebar: React.ReactNode;
  thread: React.ReactNode;
}) => (
  <Box bg="#F7F8FA" minH="100vh" px={{ base: 0, lg: 5 }} py={{ base: 0, lg: 5 }}>
    <Box
      maxW="1600px"
      mx="auto"
      h={{ base: '100vh', lg: 'calc(100vh - 40px)' }}
      bg="white"
      borderWidth={{ base: '0', lg: '1px' }}
      borderColor={{ lg: waBorder }}
      borderRadius={{ lg: '24px' }}
      overflow="hidden"
      boxShadow={{ lg: '0 18px 44px rgba(15, 23, 42, 0.08)' }}
    >
      <HStack align="stretch" spacing={0} h="100%">
        {sidebar}
        {thread}
      </HStack>
    </Box>
  </Box>
);

export const WhatsAppSidebarShell = ({ children }: { children: React.ReactNode }) => (
  <Box
    w={{ base: '100%', lg: '390px', xl: '410px' }}
    borderRightWidth={{ lg: '1px' }}
    borderColor={waBorder}
    bg="white"
    h="100%"
    display="flex"
    flexDirection="column"
  >
    {children}
  </Box>
);

export const WhatsAppSidebarHeader = ({
  title,
  subtitle,
  onRefresh,
  onSettings,
}: {
  title: string;
  subtitle?: string;
  onRefresh?: () => void;
  onSettings?: () => void;
}) => (
  <HStack
    justify="space-between"
    align="center"
    px={4}
    py={4}
    borderBottomWidth="1px"
    borderColor={waBorder}
    bg="white"
  >
    <Box>
      <Text fontSize="lg" fontWeight="800" color="gray.900">
        {title}
      </Text>
      {subtitle ? (
        <Text fontSize="sm" color="gray.500">
          {subtitle}
        </Text>
      ) : null}
    </Box>
    <HStack spacing={1}>
      {onRefresh ? (
        <IconButton
          aria-label="Refresh inbox"
          icon={<Icon as={RefreshCw as ElementType} boxSize={4} />}
          variant="ghost"
          borderRadius="14px"
          onClick={onRefresh}
        />
      ) : null}
      {onSettings ? (
        <IconButton
          aria-label="WhatsApp settings"
          icon={<Icon as={Settings as ElementType} boxSize={4} />}
          variant="ghost"
          borderRadius="14px"
          onClick={onSettings}
        />
      ) : null}
    </HStack>
  </HStack>
);

export const WhatsAppSearchBar = ({
  value,
  onChange,
  inputRef,
}: {
  value: string;
  onChange: (value: string) => void;
  inputRef?: RefObject<HTMLInputElement | null>;
}) => (
  <Box px={3} py={3} bg="white" borderBottomWidth="1px" borderColor={waBorder}>
    <InputGroup>
      <InputLeftElement pointerEvents="none">
        <Search size={16} color="#64748B" />
      </InputLeftElement>
      <Input
        ref={inputRef}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Search or start a new chat"
        borderRadius="18px"
        bg="gray.50"
        borderColor="gray.200"
        _focusVisible={{
          borderColor: 'teal.400',
          boxShadow: '0 0 0 1px rgba(18, 140, 126, 0.22)',
          bg: 'white',
        }}
      />
    </InputGroup>
  </Box>
);

export const WhatsAppSidebarEmpty = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <VStack justify="center" flex={1} spacing={3} px={6} textAlign="center">
    <Box
      boxSize="56px"
      borderRadius="full"
      bg="gray.100"
      display="flex"
      alignItems="center"
      justifyContent="center"
      color="gray.500"
    >
      <Icon as={MessageCircleMore as ElementType} boxSize={6} />
    </Box>
    <Text fontWeight="800" color="gray.900">
      {title}
    </Text>
    <Text fontSize="sm" color="gray.500" maxW="280px">
      {description}
    </Text>
  </VStack>
);

export const WhatsAppConversationRow = ({
  title,
  subtitle,
  time,
  avatarName,
  active = false,
  expired = false,
  onClick,
}: {
  title: string;
  subtitle: string;
  time?: string;
  avatarName?: string;
  active?: boolean;
  expired?: boolean;
  onClick?: () => void;
}) => (
  <Button
    variant="ghost"
    justifyContent="flex-start"
    alignItems="stretch"
    h="auto"
    px={0}
    py={0}
    borderRadius="0"
    onClick={onClick}
    _hover={{ bg: active ? '#E6FFFA' : '#F8FAFC' }}
    _active={{ bg: active ? '#CCFBF1' : '#F1F5F9' }}
    w="100%"
  >
    <HStack
      w="100%"
      align="center"
      spacing={3}
      px={4}
      py={3}
      bg={active ? '#E6FFFA' : 'transparent'}
      borderLeftWidth="3px"
      borderLeftColor={active ? waAccent : 'transparent'}
    >
      <Avatar
        size="md"
        name={avatarName || title}
        bg={active ? '#0F766E' : '#E2E8F0'}
        color={active ? 'white' : 'gray.700'}
      />
      <Box flex={1} minW={0} textAlign="left">
        <HStack justify="space-between" align="flex-start" spacing={3} mb={0.5}>
          <Text fontWeight="700" color="gray.900" noOfLines={1}>
            {title}
          </Text>
          <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
            {time}
          </Text>
        </HStack>
        <HStack spacing={2} align="center">
          <Text fontSize="sm" color="gray.500" noOfLines={1} flex={1}>
            {subtitle}
          </Text>
          {expired ? (
            <Badge colorScheme="orange" borderRadius="full" px={2} py={0.5}>
              24h
            </Badge>
          ) : null}
        </HStack>
      </Box>
    </HStack>
  </Button>
);

export const WhatsAppSidebarSkeleton = () => (
  <Stack spacing={0}>
    {Array.from({ length: 8 }).map((_, index) => (
      <HStack key={index} spacing={3} px={4} py={3}>
        <Skeleton boxSize="44px" borderRadius="full" />
        <Box flex={1}>
          <Skeleton h="12px" w="40%" mb={2} />
          <Skeleton h="11px" w="78%" />
        </Box>
      </HStack>
    ))}
  </Stack>
);

export const WhatsAppThreadPlaceholder = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <VStack
    flex={1}
    justify="center"
    align="center"
    spacing={4}
    bg="#F0F2F5"
    px={8}
    textAlign="center"
  >
    <Box
      boxSize="72px"
      borderRadius="full"
      bg="white"
      display="flex"
      alignItems="center"
      justifyContent="center"
      color={waAccent}
      boxShadow="0 12px 30px rgba(15, 23, 42, 0.06)"
    >
      <Icon as={MessageCircleMore as ElementType} boxSize={8} />
    </Box>
    <Text fontSize="2xl" fontWeight="800" color="gray.900">
      {title}
    </Text>
    <Text color="gray.500" maxW="420px">
      {description}
    </Text>
  </VStack>
);

export const WhatsAppDateSeparator = ({ label }: { label: string }) => (
  <HStack justify="center" py={2}>
    <Badge
      bg="rgba(255,255,255,0.88)"
      color="gray.600"
      px={3}
      py={1}
      borderRadius="full"
      boxShadow="sm"
      fontWeight="600"
    >
      {label}
    </Badge>
  </HStack>
);

export const WhatsAppSyncPill = ({
  label,
  tone = 'gray',
}: {
  label: string;
  tone?: 'gray' | 'orange' | 'green';
}) => (
  <HStack
    spacing={1.5}
    px={2.5}
    py={1}
    borderRadius="full"
    bg={tone === 'orange' ? 'orange.50' : tone === 'green' ? 'green.50' : 'gray.100'}
    color={tone === 'orange' ? 'orange.700' : tone === 'green' ? 'green.700' : 'gray.600'}
  >
    <Icon as={Clock3 as ElementType} boxSize={3.5} />
    <Text fontSize="xs" fontWeight="700">
      {label}
    </Text>
  </HStack>
);
